import { useEffect, useState } from 'react'
import { Search, X } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import { formatDateTime } from '@/lib/locale'
import {
  useActivateCustomerUser,
  useCustomerUsers,
  useDeactivateCustomerUser,
  useDeleteCustomerUser,
} from '@/features/platformAdmin/queries'
import type { CustomerUserRow } from '@/features/platformAdmin/types'

const fmtDate = (v: string | null) => (v ? formatDateTime(v) : '-')

const errMsg = (err: unknown, fallback = 'Action failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function CustomerUsersTab() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const [searchInput, setSearchInput] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchInput.trim()), 250)
    return () => clearTimeout(t)
  }, [searchInput])

  const usersQ = useCustomerUsers(debounced)
  const deactivate = useDeactivateCustomerUser()
  const activate = useActivateCustomerUser()
  const removeUser = useDeleteCustomerUser()
  const canManage = hasPermission('admin.users.manage')

  const [confirm, setConfirm] = useState<{
    title: string
    body: string
    onConfirm: () => Promise<void>
    confirmLabel?: string
    confirmVariant?: 'primary' | 'danger'
  } | null>(null)

  const requestDeactivate = (u: CustomerUserRow) =>
    setConfirm({
      title: 'Deactivate user',
      body: `Revoke ${u.email}'s sessions? They can be reactivated later.`,
      confirmLabel: 'Deactivate',
      onConfirm: async () => {
        try {
          await deactivate.mutateAsync(u.id)
          toast({ title: 'User deactivated.' })
        } catch (err) {
          toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
        }
      },
    })

  const doActivate = async (u: CustomerUserRow) => {
    try {
      await activate.mutateAsync(u.id)
      toast({ title: 'User activated.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  const requestDelete = (u: CustomerUserRow) =>
    setConfirm({
      title: 'Delete customer user',
      body: `Permanently delete ${u.email} and ALL their data? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
      onConfirm: async () => {
        try {
          await removeUser.mutateAsync(u.id)
          toast({ title: 'User deleted.' })
        } catch (err) {
          toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
        }
      },
    })

  const columns: ColumnDef<CustomerUserRow>[] = [
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => <span className="font-semibold">{row.original.email}</span>,
    },
    { accessorKey: 'name', header: 'Name', cell: ({ row }) => row.original.name ?? '-' },
    { accessorKey: 'tenant_name', header: 'Tenant', cell: ({ row }) => row.original.tenant_name ?? '-' },
    { accessorKey: 'plan', header: 'Plan', cell: ({ row }) => row.original.plan ?? 'free' },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }) =>
        row.original.is_active ? (
          <Badge variant="success">active</Badge>
        ) : (
          <Badge variant="danger">inactive</Badge>
        ),
    },
    {
      accessorKey: 'tokens_used',
      header: 'Tokens',
      cell: ({ row }) => (
        <span className="font-mono text-xs">{(row.original.tokens_used || 0).toLocaleString()}</span>
      ),
    },
    {
      accessorKey: 'transactions_processed',
      header: 'Txns',
      cell: ({ row }) => (
        <span className="font-mono text-xs">
          {(row.original.transactions_processed || 0).toLocaleString()}
        </span>
      ),
    },
    {
      accessorKey: 'business_name',
      header: 'Business',
      cell: ({ row }) => row.original.business_name ?? '-',
    },
    {
      accessorKey: 'last_login_at',
      header: 'Last login',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{fmtDate(row.original.last_login_at)}</span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const u = row.original
        return (
          <div className="flex flex-wrap gap-1">
            {canManage && u.is_active ? (
              <Button size="sm" variant="ghost" onClick={() => requestDeactivate(u)}>
                Deactivate
              </Button>
            ) : null}
            {canManage && !u.is_active ? (
              <Button size="sm" onClick={() => doActivate(u)}>
                Activate
              </Button>
            ) : null}
            {canManage ? (
              <Button size="sm" variant="danger" onClick={() => requestDelete(u)}>
                Delete
              </Button>
            ) : null}
          </div>
        )
      },
    },
  ]

  const data = usersQ.data?.users ?? []
  const total = usersQ.data?.total ?? 0

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            placeholder="Search by email or name…"
            className="pl-9 pr-9"
          />
          {searchInput ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>
        <span className="text-xs text-muted-foreground">
          Showing {data.length} of {total}
        </span>
      </div>
      <Card>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            rows={data}
            loading={usersQ.isLoading}
            empty="No users match."
          />
        </CardContent>
      </Card>

      <Modal.Confirm
        open={!!confirm}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.title ?? ''}
        body={confirm?.body ?? ''}
        confirmLabel={confirm?.confirmLabel ?? 'Confirm'}
        confirmVariant={confirm?.confirmVariant ?? 'primary'}
        onConfirm={async () => {
          const c = confirm
          setConfirm(null)
          if (c) await c.onConfirm()
        }}
      />
    </div>
  )
}
